import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LayoutGrid, List as ListIcon, TrendingUp } from 'lucide-react'
import SearchBar from '../components/SearchBar'
import EpisodeCard from '../components/EpisodeCard'
import SortControls from '../components/SortControls'
import { SearchService } from '../services/SearchService'
import { usePodcastContext } from '../contexts/PodcastContext'
import { getAverageScore } from '../utils/scoreUtils'

// Fonction utilitaire pour extraire le nom d'un jeu
const extractGameName = (gameDescription) => {
  if (!gameDescription) return null

  // Essayer d'extraire le nom du jeu depuis la description
  const patterns = [
    /^([^-]+) -/,  // "Nom du jeu - description"
    /^Le (.*?)(?:\s|$)/, // "Le nom du jeu"
    /^(.*?) :/,    // "Nom du jeu : description"
    /^(.*?)(?:\s-\s|$)/ // "Nom du jeu - " ou "Nom du jeu"
  ]

  for (const pattern of patterns) {
    const match = gameDescription.match(pattern)
    if (match && match[1]) {
      return match[1].trim()
    }
  }

  // Si aucun pattern ne correspond, retourner les premiers mots
  const words = gameDescription.trim().split(' ')
  return words.slice(0, Math.min(3, words.length)).join(' ')
}

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { episodes } = usePodcastContext()
  const [filteredEpisodes, setFilteredEpisodes] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState('minimal') // 'detailed' ou 'minimal'
  const [sortOption, setSortOption] = useState('date') // 'date', 'title', 'score'
  const [sortOrder, setSortOrder] = useState('asc') // 'asc' ou 'desc'
  const [podcastTypes, setPodcastTypes] = useState(['floodcast', 'podchiasse']) // Types de podcast sélectionnés
  const [filters, setFilters] = useState({
    guests: [],
    games: [],
    themes: [],
    moods: [],
    contentTypes: []
  })
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const observerRef = useRef()
  const isLoadingRef = useRef(false)
  const hasMoreEpisodesRef = useRef(true)
  const EPISODES_PER_PAGE = 10
  const MAX_DISPLAYED_EPISODES = 100 // Limite pour éviter la surcharge

  // Initialiser les épisodes filtrés quand les données sont chargées
  useEffect(() => {
    if (episodes.length > 0) {
      setFilteredEpisodes(episodes)
    }
  }, [episodes])

  // Gérer la recherche initiale depuis l'URL après chargement des données
  useEffect(() => {
    if (episodes.length > 0) {
      const urlQuery = searchParams.get('q') || ''
      setSearchQuery(urlQuery)
      
      if (urlQuery) {
        const results = SearchService.searchEpisodes(episodes, urlQuery, filters)
        setFilteredEpisodes(results)
      } else {
        setFilteredEpisodes(episodes)
      }
    }
  }, [episodes, searchParams])

  // Données pour les filtres
  const filterData = useMemo(() => {
    if (episodes.length === 0) return { guests: [], games: [], themes: [] }

    const allGuests = new Set()
    const allGames = new Set()
    const allThemes = new Set()

    episodes.forEach(episode => {
      // Gérer guests ET participants
      episode.guests?.forEach(guest => allGuests.add(guest))
      episode.participants?.forEach(participant => allGuests.add(participant))

      episode.games?.forEach(game => {
        // Extraire le nom du jeu de façon plus intelligente
        const content = game.content || String(game)
        const extractedName = extractGameName(content)
        if (extractedName) allGames.add(extractedName)
      })
      episode.themes?.forEach(theme => allThemes.add(theme))
    })

    return {
      guests: Array.from(allGuests).sort(),
      games: Array.from(allGames).sort(),
      themes: Array.from(allThemes).sort()
    }
  }, [episodes])

  // Fonction de tri et filtrage des épisodes
  const sortAndFilterEpisodes = useCallback((episodesToSort) => {
    // D'abord filtrer par type de podcast
    const filtered = episodesToSort.filter(episode => {
      // Utiliser la variable podcast directement sur l'épisode
      return podcastTypes.includes(episode.podcast)
    })
    
    // Ensuite trier
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue
      
      switch (sortOption) {
        case 'date':
          // Convertir les dates au format ISO pour comparison
          aValue = new Date(a.analysisDate || a.date || a.upload_date || '1970-01-01')
          bValue = new Date(b.analysisDate || b.date || b.upload_date || '1970-01-01')
          break
          
        case 'title':
          aValue = (a.title || '').toLowerCase()
          bValue = (b.title || '').toLowerCase()
          break
          
        case 'score':
          // Utiliser getAverageScore pour calculer le score moyen
          aValue = getAverageScore(a.analysisData || {})
          bValue = getAverageScore(b.analysisData || {})
          break
          
        default:
          return 0
      }
      
      // Gérer les comparaisons selon le type
      let comparison = 0
      if (sortOption === 'date' || sortOption === 'score') {
        comparison = aValue - bValue
      } else {
        comparison = aValue.localeCompare(bValue)
      }
      
      return sortOrder === 'asc' ? comparison : -comparison
    })
    
    return sorted
  }, [sortOption, sortOrder, podcastTypes])


  // Gérer la recherche avec useCallback pour éviter les re-renders
  const handleSearch = useCallback((query, searchFilters) => {
    // Éviter les appels inutiles si rien n'a changé
    if (query === searchQuery && JSON.stringify(searchFilters) === JSON.stringify(filters)) {
      return
    }
    setSearchQuery(query)
    setFilters(searchFilters)
    setCurrentPage(1) // Remettre à la première page
    
    // Mettre à jour l'URL
    const params = new URLSearchParams()
    if (query && query.trim()) {
      params.set('q', query)
    }
    setSearchParams(params)

    // Effectuer la recherche immédiatement
    if (query.trim() || Object.values(searchFilters).some(arr => arr.length > 0)) {
      const results = SearchService.searchEpisodes(episodes, query, searchFilters)
      setFilteredEpisodes(results)
    } else {
      setFilteredEpisodes(episodes)
    }
  }, [episodes, setSearchParams])

  // Gérer les changements de filtres par catégories
  const handleCategoryFiltersChange = useCallback((categoryFilters) => {
    const newFilters = { ...filters, ...categoryFilters }
    
    // Éviter les appels inutiles si rien n'a changé
    if (JSON.stringify(newFilters) === JSON.stringify(filters)) {
      return
    }
    setFilters(newFilters)
    setCurrentPage(1) // Remettre à la première page

    // Effectuer la recherche immédiatement
    if (searchQuery.trim() || Object.values(newFilters).some(arr => arr.length > 0)) {
      const results = SearchService.searchEpisodes(episodes, searchQuery, newFilters)
      setFilteredEpisodes(results)
    } else {
      setFilteredEpisodes(episodes)
    }
  }, [episodes, searchQuery, filters])

  // Calculer les épisodes à afficher
  const hasSearchResults = useMemo(() => {
    return searchQuery || Object.values(filters).some(arr => arr.length > 0)
  }, [searchQuery, filters])

  const allEpisodes = useMemo(() => {
    const episodesToUse = hasSearchResults ? filteredEpisodes : episodes
    return sortAndFilterEpisodes(episodesToUse)
  }, [hasSearchResults, filteredEpisodes, episodes, sortAndFilterEpisodes])
  
  // Épisodes paginés avec limite
  const displayEpisodes = useMemo(() => {
    const endIndex = Math.min(currentPage * EPISODES_PER_PAGE, MAX_DISPLAYED_EPISODES)
    return allEpisodes.slice(0, endIndex)
  }, [allEpisodes, currentPage])
  
  // Vérifier s'il y a plus d'épisodes à charger
  const hasMoreEpisodes = useMemo(() => {
    const hasMore = displayEpisodes.length < allEpisodes.length && displayEpisodes.length < MAX_DISPLAYED_EPISODES
    hasMoreEpisodesRef.current = hasMore
    return hasMore
  }, [displayEpisodes.length, allEpisodes.length])

  // Mettre à jour les refs
  useEffect(() => {
    isLoadingRef.current = isLoading
  }, [isLoading])

  // Réinitialiser l'observer quand les épisodes changent
  useEffect(() => {
    if (observerRef.current) {
      hasMoreEpisodesRef.current = displayEpisodes.length < allEpisodes.length
    }
  }, [allEpisodes, displayEpisodes.length])

  // Intersection Observer pour le scroll infini avec debouncing
  useEffect(() => {
    if (!observerRef.current) return

    let timeoutId = null
    let debounceTimer = null
    
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        
        // Debounce pour éviter trop d'appels
        if (debounceTimer) {
          clearTimeout(debounceTimer)
        }
        
        debounceTimer = setTimeout(() => {
          if (entry.isIntersecting && !isLoadingRef.current && hasMoreEpisodesRef.current) {
            // Éviter les déclenchements multiples
            if (timeoutId) return
            
            isLoadingRef.current = true
            setIsLoading(true)
            
            timeoutId = setTimeout(() => {
              setCurrentPage(prev => prev + 1)
              setIsLoading(false)
              isLoadingRef.current = false
              timeoutId = null
            }, 200) // Réduit encore le délai
          }
        }, 100) // Debounce de 100ms
      },
      { threshold: 0.3, rootMargin: '100px' } // Optimisé pour l'anticipation
    )

    observer.observe(observerRef.current)

    return () => {
      observer.disconnect()
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-dark to-background-darker">
      <div className="max-w-[1020px] mx-auto px-4 py-8">
        {/* En-tête avec titre et description */}
        <div className="text-center mb-6 md:mb-12">
          <h1 className="text-2xl md:text-5xl font-bold text-text-primary mb-4 md:mb-8 bg-gradient-to-r from-primary-color to-secondary-color bg-clip-text text-transparent">
            Explorez les podcasts : Floodcast, Podchiasse
          </h1>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed mb-2 md:mb-4">
            Parcourez facilement vos podcasts favoris
          </p>
        </div>

        {/* Barre de recherche avec filtres intégrés */}
        <div className="mb-8 sm:px-8">
          <SearchBar
            onSearch={handleSearch}
            onFilterChange={(newFilters) => {
              setFilters(newFilters)
              // Effectuer la recherche immédiatement
              if (searchQuery.trim() || Object.values(newFilters).some(arr => arr.length > 0)) {
                const results = SearchService.searchEpisodes(episodes, searchQuery, newFilters)
                setFilteredEpisodes(results)
              } else {
                setFilteredEpisodes(episodes)
              }
            }}
            availableGuests={filterData.guests}
            availableGames={filterData.games}
            availableThemes={filterData.themes}
            initialQuery={searchQuery}
            initialFilters={filters}
            episodes={episodes}
            onCategoryFiltersChange={handleCategoryFiltersChange}
          />
        </div>


        {/* Résultats de recherche */}
        <div>
          {hasSearchResults && (
            <div className="mb-10">
              <h2 className="text-2xl font-semibold text-text-primary mb-4">
                Résultats de recherche
              </h2>
              <p className="text-text-secondary mb-8">
                {filteredEpisodes.length} épisode{filteredEpisodes.length > 1 ? 's' : ''} 
                {searchQuery && ` pour "${searchQuery}"`}
                {Object.values(filters).some(arr => arr.length > 0) && ' avec filtres appliqués'}
              </p>
            </div>
          )}

          {!hasSearchResults && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
                  Tous les épisodes 
                </h2>
                
                {/* Boutons de mode d'affichage */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-muted mr-2 hidden sm:inline">Mode d'affichage :</span>
                  <button
                    onClick={() => setViewMode('minimal')}
                    className={`btn ${viewMode === 'minimal' ? 'btn-primary' : 'btn-ghost'} px-3 py-2`}
                    title="Vue minimale"
                  >
                    <ListIcon size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('detailed')}
                    className={`btn ${viewMode === 'detailed' ? 'btn-primary' : 'btn-ghost'} px-3 py-2`}
                    title="Vue détaillée"
                  >
                    <LayoutGrid size={16} />
                  </button>
                </div>
              </div>
              
              {/* Contrôles de tri */}
              <SortControls
                sortOption={sortOption}
                setSortOption={setSortOption}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                podcastTypes={podcastTypes}
                setPodcastTypes={setPodcastTypes}
              />
            </div>
          )}

          {/* Liste des épisodes */}
          {displayEpisodes.length > 0 ? (
            <div className="grid grid-cols-1 gap-6">
              {displayEpisodes.map((episode) => (
                <EpisodeCard
                  key={episode.id}
                  episode={episode}
                  viewMode={viewMode}
                />
              )
              )}
            </div>
          ) : (
            <div className="card text-center py-16">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-background-light flex items-center justify-center">
                  <TrendingUp size={32} className="text-text-muted" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">
                  Aucun résultat trouvé
                </h3>
                <p className="text-text-secondary mb-6">
                  Essayez de modifier votre recherche ou de supprimer certains filtres.
                </p>
                <button
                  onClick={() => handleSearch('', { guests: [], games: [], themes: [] })}
                  className="btn btn-primary"
                >
                  Effacer la recherche
                </button>
              </div>
            </div>
          )}
          
          {/* Élément observateur pour le scroll infini */}
          {displayEpisodes.length > 0 && hasMoreEpisodes && (
            <div ref={observerRef} className="flex justify-center py-8">
              {isLoading ? (
                <div className="flex items-center gap-2 text-text-muted">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-color border-t-transparent"></div>
                  <span>Chargement...</span>
                </div>
              ) : (
                <div className="text-text-muted text-sm">
                  Faites défiler pour voir plus d'épisodes
                </div>
              )}
            </div>
          )}
          
          {/* Indicateur de fin */}
          {displayEpisodes.length > 0 && !hasMoreEpisodes && (
            <div className="text-center py-8 text-text-muted text-sm">
              {displayEpisodes.length >= MAX_DISPLAYED_EPISODES ? (
                <div>
                  <p>Limite de {MAX_DISPLAYED_EPISODES} épisodes atteinte pour optimiser les performances</p>
                  <p className="mt-2">Total disponible : {allEpisodes.length} épisodes</p>
                  <p className="mt-1 text-xs">Utilisez la recherche pour affiner vos résultats</p>
                </div>
              ) : (
                <p>Vous avez vu tous les épisodes ({allEpisodes.length})</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchPage 